'use client';

import { useLayoutEffect, type RefObject } from 'react';

const themeProperties = ['--background', '--foreground', '--primary', '--primary-foreground', '--popover', '--popover-foreground', '--muted', '--muted-foreground', '--accent', '--accent-foreground', '--border', '--ring', '--destructive', '--field-border', '--field-surface', '--field-focus-shadow', '--dropdown-surface', '--dropdown-shadow', '--community-bg', '--community-fg', '--community-dim', '--community-muted', '--community-border'];

/** Keep a portaled panel anchored and themed, including nested theme changes. */
export function useAnchoredPanel(open: boolean, anchor: RefObject<HTMLElement | null>, panel: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    if (!open || !anchor.current || !panel.current) return;
    const trigger = anchor.current, element = document.getElementById(panel.current.id);
    if (!element) return;
    const popup = element;
    // A manual popover stays in the same DOM/focus scope while escaping a
    // transformed or overflow-clipped dialog ancestor's coordinate system.
    if (popup.hasAttribute('popover') && typeof popup.showPopover === 'function') popup.showPopover();
    function update() {
      const theme = getComputedStyle(trigger);
      for (const property of themeProperties) popup.style.setProperty(property, theme.getPropertyValue(property));
      popup.style.fontFamily = theme.fontFamily;
      popup.dir = theme.direction;
      const field = trigger.getBoundingClientRect(), bounds = popup.getBoundingClientRect();
      const preferredLeft = theme.direction === 'rtl' ? field.right - bounds.width : field.left;
      popup.style.left = `${Math.max(12, Math.min(preferredLeft, innerWidth - bounds.width - 12))}px`;
      const below = field.bottom + 6;
      popup.style.top = `${Math.max(12, Math.min(below + bounds.height <= innerHeight - 12 ? below : field.top - bounds.height - 6, innerHeight - bounds.height - 12))}px`;
    }
    update();
    const resize = new ResizeObserver(update); resize.observe(popup); resize.observe(trigger);
    const themes = new MutationObserver(update);
    for (let element: HTMLElement | null = trigger; element; element = element.parentElement) themes.observe(element, { attributes: true, attributeFilter: ['class', 'style', 'dir'] });
    window.addEventListener('resize', update); window.addEventListener('scroll', update, true);
    return () => { resize.disconnect(); themes.disconnect(); window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true); if (popup.matches(':popover-open')) popup.hidePopover(); };
  }, [open, anchor, panel]);
}
